import React from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import MeuPerfil from './components/MeuPerfil';
import AdminPanel from './components/AdminPanel';
import SessaoDetalhe from './components/SessaoDetalhe';
import ChangePassword from './components/ChangePassword';
import GestaoAvaliadores from './components/GestaoAvaliadores';
import ResultadosPremio from './components/ResultadosPremio';
import AvaliacaoPremio from './components/AvaliacaoPremio';
import PrimeiroAcesso from './components/PrimeiroAcesso';
import LogAcessos from './components/LogAcessos';
import SplashScreen from './components/SplashScreen';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

function AppNavigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = React.useState(null);
  const navRef = React.useRef(null);

  React.useEffect(() => {
    const fecharAoClicarFora = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) setOpenMenu(null);
    };
    const fecharComEscape = (event) => {
      if (event.key === 'Escape') setOpenMenu(null);
    };
    document.addEventListener('mousedown', fecharAoClicarFora);
    document.addEventListener('keydown', fecharComEscape);
    return () => {
      document.removeEventListener('mousedown', fecharAoClicarFora);
      document.removeEventListener('keydown', fecharComEscape);
    };
  }, []);

  // Toda mudança de tela fecha o menu anterior, evitando sobreposição ou
  // destaque visual remanescente durante a troca de categoria.
  React.useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname]);

  const toggleMenu = (menu) => setOpenMenu(atual => atual === menu ? null : menu);
  const fecharMenu = () => setOpenMenu(null);

  const handleLogout = async () => {
    try { await logout(); navigate('/login'); }
    catch (error) { console.error("Erro ao fazer logout:", error); }
  };

  // A partir do CBACV2026, as 3 categorias (Temas Livres, Pôsteres, Temas Correlatos)
  // usam o mesmo modelo de avaliação/premiação (Diethrich) e as mesmas regras de acesso.
  const podeVerResultados = user && (user.is_superuser || user.is_coordenador);
  const podeVerAdmin = user && (user.is_superuser || user.is_staff || user.is_coordenador);

  const categorias = [
    { chave: 'temas_livres',     label: 'Temas Livres',  emoji: '📋' },
    { chave: 'posteres',         label: 'Pôsteres',      emoji: '🖼️' },
    { chave: 'temas_correlatos', label: 'T. Correlatos', emoji: '🎤' },
  ];

  return (
    <header className="App-header">
      <div className="app-brand" aria-label="Evalon">
        <span className="app-brand-mark">E</span>
        <span className="app-brand-name">Evalon</span>
      </div>
      <nav className="app-nav" aria-label="Navegação principal" ref={navRef}>
        {user && (
          <>
            <div className={`nav-menu ${openMenu === 'avaliar' ? 'is-open' : ''}`}>
              <button className="nav-menu-trigger" type="button" onClick={() => toggleMenu('avaliar')} aria-expanded={openMenu === 'avaliar'}>
                <span className="nav-menu-icon">✎</span> Avaliar <span className="nav-chevron">⌄</span>
              </button>
              {openMenu === 'avaliar' && <div className="nav-dropdown">
                <span className="nav-dropdown-title">Comissões de avaliação</span>
                {categorias.map(c => (
                  <NavLink key={'av-' + c.chave} to={`/avaliar/${c.chave}`} end onClick={fecharMenu}>
                    <span>{c.emoji}</span><span>{c.label}<small>Acessar trabalhos</small></span>
                  </NavLink>
                ))}
              </div>}
            </div>

            <a
              className="nav-tutorial-button"
              href="/api/tutorial-avaliador/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={fecharMenu}
              aria-label="Abrir Tutorial do Avaliador em PDF"
              title="Abrir Tutorial do Avaliador em PDF"
            >
              <span className="nav-menu-icon">📖</span> Tutorial
            </a>

            <div className={`nav-menu ${openMenu === 'gestao' ? 'is-open' : ''}`}>
              <button className="nav-menu-trigger" type="button" onClick={() => toggleMenu('gestao')} aria-expanded={openMenu === 'gestao'}>
                <span className="nav-menu-icon">⚙</span> Gestão <span className="nav-chevron">⌄</span>
              </button>
              {openMenu === 'gestao' && <div className="nav-dropdown">
                <span className="nav-dropdown-title">Administração</span>
                <NavLink to="/meu-perfil" onClick={fecharMenu}><span>👤</span><span>Meu perfil<small>Dados e segurança</small></span></NavLink>
                {podeVerAdmin && <NavLink to="/admin-panel" onClick={fecharMenu}><span>▦</span><span>Painel Admin<small>Sessões e trabalhos</small></span></NavLink>}
                {user.is_admin_premio && !user.is_staff && !user.is_superuser && !user.is_coordenador && <NavLink to="/admin-panel" onClick={fecharMenu}><span>🏆</span><span>Premiação<small>Controle de avaliações</small></span></NavLink>}
                {(user.is_coordenador || user.is_superuser) && <NavLink to="/gestao-avaliadores" onClick={fecharMenu}><span>♟</span><span>Avaliadores<small>Usuários e credenciais</small></span></NavLink>}
                {user.is_superuser && <NavLink to="/logs" onClick={fecharMenu}><span>≡</span><span>Logs de acesso<small>Histórico do sistema</small></span></NavLink>}
              </div>}
            </div>

            {podeVerResultados && <div className={`nav-menu ${openMenu === 'resultados' ? 'is-open' : ''}`}>
              <button className="nav-menu-trigger" type="button" onClick={() => toggleMenu('resultados')} aria-expanded={openMenu === 'resultados'}>
                <span className="nav-menu-icon">▥</span> Resultados <span className="nav-chevron">⌄</span>
              </button>
              {openMenu === 'resultados' && <div className="nav-dropdown nav-dropdown-right">
                <span className="nav-dropdown-title">Classificação por categoria</span>
                {categorias.map(c => (
                  <NavLink key={'res-' + c.chave} to={`/resultados/${c.chave}`} end onClick={fecharMenu}>
                    <span>{c.emoji}</span><span>{c.label}<small>Ver classificação</small></span>
                  </NavLink>
                ))}
              </div>}
            </div>}
          </>
        )}
      </nav>
      {user && (
        <div className="user-info">
          <div className="user-avatar">{user.username?.charAt(0).toUpperCase()}</div>
          <div className="user-copy">
            <small>Bem-vindo</small>
            <span>{user.username}</span>
          </div>
          <button onClick={handleLogout} className="logout-button" aria-label="Sair da plataforma">Sair</button>
        </div>
      )}
    </header>
  );
}

// Resultados oficiais são exclusivos do superusuário e dos coordenadores.
function ResultadosRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div>Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_superuser || user.is_coordenador) return children;
  return <Navigate to="/avaliar/temas_livres" replace />;
}

function PrimeiroAcessoGuard() {
  const { user } = useAuth();
  if (user && !user.is_coordenador && !user.is_superuser && !user.is_admin_premio && (!user.first_name || !user.last_name)) {
    return <PrimeiroAcesso />;
  }
  return null;
}

const CATEGORIAS_VALIDAS = ['temas_livres', 'posteres', 'temas_correlatos'];

// Wrappers de rota — leem :categoria da URL e repassam ao componente genérico
function AvaliarCategoriaRoute() {
  const { categoria } = useParams();
  if (!CATEGORIAS_VALIDAS.includes(categoria)) return <Navigate to="/avaliar/temas_livres" replace />;
  return <AvaliacaoPremio categoria={categoria} />;
}

function ResultadosCategoriaRoute() {
  const { categoria } = useParams();
  if (!CATEGORIAS_VALIDAS.includes(categoria)) return <Navigate to="/resultados/temas_livres" replace />;
  return <ResultadosPremio categoria={categoria} />;
}

function App() {
  const [showSplash, setShowSplash] = React.useState(true);

  const finalizarSplash = React.useCallback(() => {
    setShowSplash(false);
  }, []);

  if (showSplash) {
    return <SplashScreen onFinish={finalizarSplash} />;
  }

  return (
    <Router>
      <div className="App">
        <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false}
          newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />
        <AppNavigation />
        <PrimeiroAcessoGuard />
        <main>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute><Navigate to="/avaliar/temas_livres" replace /></ProtectedRoute>} />
            <Route path="/avaliar/:categoria" element={<ProtectedRoute><AvaliarCategoriaRoute /></ProtectedRoute>} />
            <Route path="/resultados/:categoria" element={<ResultadosRoute><ResultadosCategoriaRoute /></ResultadosRoute>} />
            <Route path="/meu-perfil" element={<ProtectedRoute><MeuPerfil /></ProtectedRoute>} />
            <Route path="/admin-panel" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
            <Route path="/admin-panel/sessao/:id" element={<ProtectedRoute><SessaoDetalhe /></ProtectedRoute>} />
            <Route path="/meu-perfil/alterar-senha" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/gestao-avaliadores" element={<ProtectedRoute><GestaoAvaliadores /></ProtectedRoute>} />
            <Route path="/logs" element={<ProtectedRoute><LogAcessos /></ProtectedRoute>} />
          </Routes>
        </main>
        <footer className="App-footer">
          <p>© 2026 Evalon · Desenvolvido por NimVexx · Mahfouz Tech</p>
        </footer>
      </div>
    </Router>
  );
}

export default App;
